import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Textarea,
} from "@capacit-com/lara-law-shared-ui";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import { useConsolidationFeedback } from "@/hooks/useConsolidation";
import { getConsolidationContainerTypeLabel } from "@/lib/consolidationContainerLabels";
import {
  ConsolidationFeedbackCategory,
  ConsolidationView,
} from "@/models/consolidation";

const CATEGORY_OPTIONS: {
  value: ConsolidationFeedbackCategory;
  label: string;
}[] = [
  { value: "incorrect_content", label: "Forkert indhold" },
  { value: "wrong_date", label: "Forkert dato" },
  { value: "formatting", label: "Formatering" },
  { value: "other", label: "Andet" },
];

const formSchema = z.object({
  category: z.enum(["incorrect_content", "wrong_date", "formatting", "other"]),
  message: z
    .string()
    .min(1, { message: "Udfyld en beskrivelse" })
    .max(4000, { message: "Beskrivelsen må højst være 4000 tegn" }),
});

type FormValues = z.infer<typeof formSchema>;

type ConsolidationFeedbackFormProps = {
  lawExternalId: string;
  asOf: string;
  view: ConsolidationView;
  containerId: string;
  containerType?: string | null;
  onSuccess?: () => void;
  onCancel?: () => void;
  compact?: boolean;
};

const ConsolidationFeedbackForm: FC<ConsolidationFeedbackFormProps> = ({
  lawExternalId,
  asOf,
  view,
  containerId,
  containerType,
  onSuccess,
  onCancel,
  compact = false,
}) => {
  const submitFeedback = useConsolidationFeedback();
  const containerTypeLabel = getConsolidationContainerTypeLabel(containerType);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      category: "incorrect_content",
      message: "",
    },
  });

  useEffect(() => {
    form.reset({ category: "incorrect_content", message: "" });
  }, [containerId, form]);

  const handleSubmit = async (values: FormValues) => {
    try {
      await submitFeedback.mutateAsync({
        lawExternalId,
        containerId,
        asOf,
        view,
        message: values.message,
        category: values.category,
      });
      showSuccessToast("Tak for din tilbagemelding");
      form.reset({ category: "incorrect_content", message: "" });
      onSuccess?.();
    } catch {
      showErrorToast("Tilbagemeldingen kunne ikke sendes");
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className={compact ? "space-y-3" : "space-y-6"}
      >
        <p className="text-sm text-muted-foreground">
          Fortæl os, hvad der er galt med dette afsnit
          {containerTypeLabel ? ` (${containerTypeLabel})` : ""}. Din
          tilbagemelding sendes til redaktionen.
        </p>

        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Type</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Vælg type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Beskrivelse</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  rows={compact ? 3 : 5}
                  placeholder="Beskriv hvad der er galt..."
                  data-testid="consolidation-feedback-message"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Annuller
            </Button>
          )}
          <Button
            type="submit"
            className="min-w-[100px]"
            disabled={submitFeedback.isPending}
          >
            {submitFeedback.isPending ? (
              <Spinner size="small" className="text-primary-foreground" />
            ) : (
              "Send"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default ConsolidationFeedbackForm;
